import { createFileRoute } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import {
  ArquivosMockup,
  BriefingMockup,
  CalendarioMockup,
  ContratoMockup,
  DocumentoMockup,
  FunilMockup,
  PortalMockup,
  PropostaPublicaMockup,
  RelatorioMockup,
  TarefasQuadroMockup,
  VisitasAgendaMockup,
} from '~/components/marketing/mockups/modules'
import { WorkspaceMockup } from '~/components/marketing/mockups/workspace-mockup'
import {
  CtaLink,
  PainsSection,
  PranchetaFaq,
  PranchetaFinalCta,
  PranchetaFooter,
  PranchetaNav,
  PranchetaPage,
  PranchetaPricing,
  PranchetaWhatsApp,
  ProductRows,
  RoughUnderline,
  SERIF,
} from '~/components/marketing/prancheta-kit'
import { ScrollReveal } from '~/components/marketing/scroll-reveal'
import { MarketingLocaleProvider } from '~/lib/marketing-locale'
import { cn } from '~/lib/utils'

/*
 * Design exploration "Prancheta" — the synthesis round.
 *
 * Headline written over a pale watercolor painting (from the -fundo
 * exploration), the real product mockup anchoring the hero (from -app),
 * and mockup-driven feature rows with watercolor garnish. Siblings kept
 * for comparison: /lp/prancheta-app, /lp/prancheta-fundo.
 *
 * Preview-only route — not linked from anywhere, noindexed.
 */

export const Route = createFileRoute('/lp/prancheta')({
  component: RouteComponent,
  head: () => ({
    meta: [
      { title: 'Planno — proposta de design "Prancheta"' },
      { content: 'noindex, nofollow', name: 'robots' },
    ],
  }),
})

/**
 * A named stretch of the index. A dozen screens in one masonry read as a
 * wall, so the modules are grouped the way the office actually divides
 * them — winning the work, doing it, handing it over — each group
 * announced by a rule and a serif line. The assistant sits outside this
 * index, in its own band.
 */
const ModuleGroup = ({
  anchor,
  body,
  children,
  name,
}: {
  /** A wide screen that leads the group at full width, above the masonry. */
  anchor?: ReactNode
  body: string
  children: ReactNode
  name: string
}) => (
  <ScrollReveal className="mt-20 border-[#29251e]/12 border-t pt-9 first:mt-14">
    <h3 className={cn('font-semibold text-[clamp(21px,2.4vw,26px)] leading-tight', SERIF)}>
      {name}
    </h3>
    <p className="mt-2.5 max-w-[600px] text-[#6f6a5c] text-[15px] leading-relaxed">{body}</p>
    {anchor ? <div className="mt-9">{anchor}</div> : null}
    <div className={cn('columns-1 gap-12 md:columns-2 xl:columns-3', anchor ? 'mt-10' : 'mt-9')}>
      {children}
    </div>
  </ScrollReveal>
)

/** One module of the mosaic: a real product mockup with a quiet caption. */
const MosaicCell = ({
  body,
  children,
  name,
}: {
  body: string
  children: ReactNode
  name: string
}) => (
  <div className="mb-12 break-inside-avoid">
    {children}
    <div className="mt-3.5 flex flex-wrap items-baseline gap-x-2 gap-y-1">
      <p className="font-semibold text-[#29251e] text-[15px]">{name}</p>
      <p className="text-[#6f6a5c] text-[13.5px]">{body}</p>
    </div>
  </div>
)

function RouteComponent() {
  return (
    <MarketingLocaleProvider locale="pt-BR">
      <PranchetaPage>
        <PranchetaNav homeHref="/lp/prancheta" />

        <main className="relative z-10">
          {/* ---------- Hero: headline over the painting, app below ---------- */}
          <section className="relative">
            {/* The painting occupies the top of the sheet; its blank
             * upper-left hosts the type, and it fades out before the
             * mockup takes over. */}
            <div className="absolute inset-x-0 top-0 h-[min(86vw,760px)] bg-[#faf8f2]">
              <img
                alt=""
                aria-hidden="true"
                className="h-full w-full object-cover object-right-bottom mix-blend-multiply [mask-image:linear-gradient(to_bottom,#000_62%,transparent_97%)]"
                height={1536}
                src="/illustrations/prancheta/fundo-hero.jpg"
                width={2752}
              />
            </div>

            <div className="relative mx-auto max-w-[1160px] px-6 pt-20 lg:pt-24">
              <h1
                className={cn(
                  'max-w-[720px] font-semibold text-[clamp(42px,5.8vw,72px)] leading-[1.04] tracking-[-0.015em]',
                  SERIF
                )}
              >
                Do briefing à{' '}
                <span className="relative whitespace-nowrap">
                  entrega das chaves
                  <RoughUnderline />
                </span>
                , <em className="font-light italic tracking-[-0.01em]">tudo na mesma prancheta.</em>
              </h1>
              <p className="mt-7 max-w-[520px] text-[#5f594c] text-[clamp(16.5px,2vw,19px)] leading-relaxed">
                CRM, propostas, projetos, sprints, visitas técnicas, financeiro e IA — a ferramenta
                profissional construída para a forma como escritórios de arquitetura realmente
                trabalham.
              </p>
              <div className="mt-9 flex flex-wrap items-center gap-3.5">
                <CtaLink href="/sign-up">Começar grátis</CtaLink>
                <CtaLink href="#planos" variant="ghost">
                  Ver planos
                </CtaLink>
              </div>
              <p className="mt-5 text-[#8d8471] text-[13.5px]">
                Plano gratuito para até 2 pessoas · sem cartão de crédito
              </p>
            </div>

            {/* The app itself, rising out of the painted garden. */}
            <div className="relative mx-auto mt-[clamp(48px,9vw,120px)] max-w-[1240px] px-6">
              <WorkspaceMockup />
            </div>
          </section>

          <PainsSection />

          <ProductRows />

          {/* ---------- Modules mosaic: the product itself as the index ---------- */}
          <section className="mx-auto max-w-[1160px] px-6 py-24" id="modulos">
            <ScrollReveal>
              <h2
                className={cn(
                  'max-w-[560px] font-semibold text-[clamp(30px,4vw,44px)] leading-[1.1] tracking-[-0.01em]',
                  SERIF
                )}
              >
                Tudo o que o escritório usa, na mesma folha
              </h2>
              <p className="mt-4 max-w-[560px] text-[#5f594c] text-[16px] leading-relaxed">
                Cada módulo conectado pelo mesmo contato, pelo mesmo projeto e pela mesma equipe —
                do primeiro lead ao portal que o cliente acompanha.
              </p>
            </ScrollReveal>

            {/* 1 — winning the work. The funnel leads at full width. */}
            <ModuleGroup
              anchor={
                <MosaicCell
                  body="Cada lead com sua origem, seu valor e seu follow-up — do primeiro contato até a zona de Ganho, onde vira projeto."
                  name="Funil e CRM"
                >
                  <FunilMockup />
                </MosaicCell>
              }
              body="O briefing que o cliente responde no celular, a proposta que ele aceita pelo link e o contrato que ele assina — tudo preso ao mesmo contato."
              name="Do primeiro contato ao contrato assinado"
            >
              <MosaicCell
                body="Mande um link, o cliente responde no celular com fotos de referência, e as respostas viram um lead com contexto no funil."
                name="Briefings"
              >
                <BriefingMockup />
              </MosaicCell>
              <MosaicCell
                body="A proposta sai no papel timbrado do escritório. O cliente abre pelo link, aceita ou recusa, e o aceite eletrônico fica registrado."
                name="Propostas"
              >
                <PropostaPublicaMockup />
              </MosaicCell>
              <MosaicCell
                body="Objeto, prazos, honorários e responsabilidade técnica já no esqueleto. As parcelas saem do valor combinado e o cliente assina pelo celular."
                name="Contratos"
              >
                <ContratoMockup />
              </MosaicCell>
            </ModuleGroup>

            {/* 2 — doing the work. */}
            <ModuleGroup
              body="O que a equipe abre todo dia: as tarefas na fase certa da obra, a semana na agenda, a visita com checklist e o arquivo na versão certa."
              name="O trabalho de cada projeto"
            >
              <MosaicCell
                body="Quadro, lista ou Gantt. Cada tarefa carrega a fase da obra, a prioridade, o intervalo de datas e quem responde por ela."
                name="Tarefas"
              >
                <TarefasQuadroMockup />
              </MosaicCell>
              <MosaicCell
                body="Visitas, entregas e o fechamento do sprint na mesma semana, do jeito que a agenda do escritório realmente é."
                name="Calendário"
              >
                <CalendarioMockup />
              </MosaicCell>
              <MosaicCell
                body="A próxima visita já com checklist e participantes, e o histórico de tudo o que foi medido, fotografado e apontado em cada obra."
                name="Visitas"
              >
                <VisitasAgendaMockup />
              </MosaicCell>
              <MosaicCell
                body="A versão certa do arquivo certo, com miniatura de verdade e o histórico de revisões que a obra precisa conferir."
                name="Arquivos"
              >
                <ArquivosMockup />
              </MosaicCell>
            </ModuleGroup>

            {/* 3 — handing it over. */}
            <ModuleGroup
              body="O que sai do escritório com a cara do escritório: o portal que o cliente acompanha, o relatório do mês e os documentos da etapa."
              name="O que chega ao cliente"
            >
              <MosaicCell
                body="O projeto pelos olhos do cliente: andamento, as fotos novas da obra e a lista de louças e metais com o total estimado."
                name="Portal do cliente"
              >
                <PortalMockup />
              </MosaicCell>
              <MosaicCell
                body="O mês fechado com as fotos da obra e os números do projeto, pronto para sair em PDF, link público ou WhatsApp."
                name="Relatório mensal"
              >
                <RelatorioMockup />
              </MosaicCell>
              <MosaicCell
                body="Memoriais, atas e termos escritos a várias mãos, com tabelas de especificação e comentários no ponto exato do texto."
                name="Documentos"
              >
                <DocumentoMockup />
              </MosaicCell>
              <div className="mb-12 break-inside-avoid">
                <div className="bg-[#faf8f2]">
                  <img
                    alt="Aquarela de instrumentos de desenho de arquitetura"
                    className="w-full mix-blend-multiply [mask-image:radial-gradient(closest-side,#000_58%,transparent_100%)]"
                    height={2048}
                    loading="lazy"
                    src="/illustrations/prancheta/detalhe.jpg"
                    width={2048}
                  />
                </div>
              </div>
            </ModuleGroup>
          </section>

          {/* The assistant earns a band of its own, not a cell in the index. */}
          <PranchetaWhatsApp />

          <PranchetaFaq />
          <PranchetaPricing />
          <PranchetaFinalCta />
        </main>

        <PranchetaFooter />
      </PranchetaPage>
    </MarketingLocaleProvider>
  )
}
